import { useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useActivityTracker = (programId) => {
  const { user } = useAuth();
  const sectionStartTime = useRef(null);
  const currentSection = useRef(null);

  const track = useCallback((activityType, itemId, itemTitle, extra = {}) => {
    if (!user?.id) return;
    fetch(`${BACKEND_URL}/api/user/track-activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: user.id,
        user_email: user.email,
        activity_type: activityType,
        program_id: programId,
        item_id: String(itemId),
        item_title: itemTitle,
        ...extra,
      }),
    }).catch(() => {});
  }, [user, programId]);

  // Track section/lesson view
  const trackView = useCallback((itemId, itemTitle) => {
    // Log time spent on previous section
    if (sectionStartTime.current && currentSection.current) {
      const seconds = Math.round((Date.now() - sectionStartTime.current) / 1000);
      if (seconds > 3) {
        track('time_spent', currentSection.current.id, currentSection.current.title, { duration_seconds: seconds });
      }
    }
    sectionStartTime.current = Date.now();
    currentSection.current = { id: itemId, title: itemTitle };
    track('lesson_view', itemId, itemTitle);
  }, [track]);

  // Track video play
  const trackVideoPlay = useCallback((itemId, itemTitle) => {
    track('video_play', itemId, itemTitle);
  }, [track]);

  // Track external link click
  const trackLinkClick = useCallback((url, linkText) => {
    track('link_click', url, linkText);
  }, [track]);

  // Track mark complete
  const trackComplete = useCallback((itemId, itemTitle) => {
    track('mark_complete', itemId, itemTitle);
  }, [track]);

  // Track section click (Food Guide, Progress Charts, etc.)
  const trackSectionClick = useCallback((sectionId, sectionTitle) => {
    track('section_view', sectionId, sectionTitle);
  }, [track]);

  // Log time on unmount
  useEffect(() => {
    return () => {
      if (sectionStartTime.current && currentSection.current && user?.id) {
        const seconds = Math.round((Date.now() - sectionStartTime.current) / 1000);
        if (seconds > 3) {
          // Use sendBeacon for reliability on page unload
          const data = JSON.stringify({
            user_id: user.id,
            user_email: user.email,
            activity_type: 'time_spent',
            program_id: programId,
            item_id: String(currentSection.current.id),
            item_title: currentSection.current.title,
            duration_seconds: seconds,
          });
          navigator.sendBeacon?.(`${BACKEND_URL}/api/user/track-activity`, new Blob([data], { type: 'application/json' }));
        }
      }
    };
  }, [user, programId]);

  return { track, trackView, trackVideoPlay, trackLinkClick, trackComplete, trackSectionClick };
};
